// ----- Scoring HSL pondere avec severite progressive -----
// Pas de LAB, pas de Delta E. Score directement comparable aux valeurs HSL affichees.

export function hueDistance(h1, h2) {
  const diff = Math.abs(h1 - h2);
  return Math.min(diff, 360 - diff);
}

// Score 0-100 avec 2 decimales. Plus c'est haut, mieux c'est.
// La severite augmente avec le niveau : multiplicateur de penalite 1.0 (niveau 1)
// a 2.0 (niveau 30) a 3.0 (niveau 60). Meme erreur = score plus bas a mesure
// qu'on avance, et chaque imperfection coute 3x plus cher au niveau 60.
//
// Pondration : H = 0.45 (le plus visible), S = 0.25, L = 0.30.
export function calculateScore(target, userColor, level = 1) {
  const hDiff = hueDistance(target.h, userColor.h);
  const sDiff = Math.abs(target.s - userColor.s);
  const lDiff = Math.abs(target.l - userColor.l);

  const hP = 100 * (1 - hDiff / 180); // teinte: max 180 d'ecart
  const sP = 100 * (1 - sDiff / 100);
  const lP = 100 * (1 - lDiff / 100);

  const rawScore = hP * 0.45 + sP * 0.25 + lP * 0.30;

  const safeLevel = Math.max(1, Math.min(60, level));
  const severity = 1.0 + ((safeLevel - 1) / 59) * 2.0;
  const penalty = (100 - rawScore) * severity;
  const finalScore = parseFloat(
    Math.max(0, Math.min(100, 100 - penalty)).toFixed(2)
  );

  return { score: finalScore, hDiff, sDiff, lDiff };
}

export function getXpFromScore(score) {
  if (score >= 95) return 30;
  if (score >= 85) return 25;
  if (score >= 70) return 18;
  if (score >= 50) return 10;
  if (score >= 25) return 5;
  return 2;
}

// Feedback educatif : commente le plus grand ecart HSL, en termes simples.
// Prend hDiff/sDiff/lDiff calcules par calculateScore.
// language : 'fr' | 'en' - choisit la langue du feedback affiche.
export function getFeedbackFromScore(score, hDiff, sDiff, lDiff, language = 'fr') {
  const isFr = language === 'fr';
  // Normalise sur la meme echelle (0-1) pour comparer
  const hRel = hDiff / 180;
  const sRel = sDiff / 100;
  const lRel = lDiff / 100;
  const maxRel = Math.max(hRel, sRel, lRel);

  let title;
  let detail;

  if (score >= 95) {
    title = isFr ? 'Difference invisible !' : 'Invisible difference!';
    detail = isFr
      ? 'Ton oeil est d\'une precision remarquable.'
      : 'Your eye is remarkably precise.';
  } else if (score >= 85) {
    title = isFr ? 'Quasi parfait !' : 'Almost perfect!';
    if (maxRel === hRel) {
      detail = isFr
        ? 'La teinte etait toute proche, juste un cheveu de difference.'
        : 'The hue was very close, just a hair off.';
    } else if (maxRel === sRel) {
      detail = isFr
        ? 'La vivacite etait legerement differente.'
        : 'The vividness was slightly different.';
    } else {
      detail = isFr
        ? 'La clarte etait le dernier ajustement.'
        : 'Lightness was the last adjustment.';
    }
  } else if (score >= 70) {
    title = isFr ? 'Tres bel oeil !' : 'Great eye!';
    if (maxRel === hRel) {
      detail = isFr
        ? 'Concentre-toi sur la teinte : ta couleur penchait un peu a cote.'
        : 'Focus on the hue: your colour leaned a little off.';
    } else if (maxRel === sRel) {
      detail = isFr
        ? 'La saturation etait l\'ecart principal - plus ou moins vive que la cible.'
        : 'Saturation was the main gap - more or less vivid than the target.';
    } else {
      detail = isFr
        ? 'Ajuste la luminosite : ta couleur etait un peu plus claire ou foncee.'
        : 'Adjust the lightness: your colour was a bit lighter or darker.';
    }
  } else if (score >= 50) {
    title = isFr ? 'Belle progression !' : 'Nice progress!';
    if (maxRel === hRel) {
      detail = isFr
        ? 'La teinte est l\'ecart majeur. Memorise mieux la famille de couleur.'
        : 'The hue is the main gap. Memorise the colour family better.';
    } else if (maxRel === sRel) {
      detail = isFr
        ? 'La saturation etait franchement differente. Observe si la couleur est vive ou douce.'
        : 'Saturation was clearly different. Notice whether the colour is vivid or soft.';
    } else {
      detail = isFr
        ? 'La clarte etait franchement differente. Plus claire ou plus foncee qu\'il fallait.'
        : 'Lightness was clearly different. Lighter or darker than it should be.';
    }
  } else if (score >= 25) {
    title = isFr ? 'Ca vient !' : 'Getting there!';
    detail = isFr
      ? 'L\'ecart se voit. Concentre-toi d\'abord sur la teinte, puis affine.'
      : 'The gap shows. Focus on the hue first, then refine.';
  } else {
    title = isFr ? 'Chaque essai compte !' : 'Every try counts!';
    detail = isFr
      ? 'Observe bien la teinte, la vivacite et la clarte avant de jouer.'
      : 'Observe the hue, vividness and lightness carefully before playing.';
  }

  return { title, detail };
}

// Message de tete (au-dessus du score)
// language : 'fr' | 'en'
export function getMessageFromScore(score, language = 'fr') {
  const isFr = language === 'fr';
  if (score >= 95) return isFr ? 'Incroyable ! Ton oeil est ultra precis !' : 'Incredible! Your eye is ultra precise!';
  if (score >= 85) return isFr ? 'Quasi parfait !' : 'Almost perfect!';
  if (score >= 70) return isFr ? 'Tres bel oeil !' : 'Great eye!';
  if (score >= 50) return isFr ? 'Belle progression !' : 'Nice progress!';
  if (score >= 25) return isFr ? 'Ca vient !' : 'Getting there!';
  return isFr ? 'Chaque essai compte !' : 'Every try counts!';
}

// ----- Helpers d'affichage -----

export function hslToString(h, s, l) {
  return `hsl(${h}, ${s}%, ${l}%)`;
}

export function formatHsl(hsl) {
  return `H${Math.round(hsl.h)} S${Math.round(hsl.s)} L${Math.round(hsl.l)}`;
}

// ----- Helpers temps / seed (pour daily challenge) -----

export function seededColorFromParams(seed, params) {
  const a = (seed * 9301 + 49297) % 233280;
  const b = (a * 9301 + 49297) % 233280;
  const c = (b * 9301 + 49297) % 233280;
  const h = Math.floor((a / 233280) * 360);
  const s = params.sMin + Math.floor((b / 233280) * (params.sMax - params.sMin));
  const l = params.lMin + Math.floor((c / 233280) * (params.lMax - params.lMin));
  return { h, s, l };
}

export function dateToSeed(dateString) {
  return parseInt(String(dateString).replace(/-/g, ''), 10);
}

export function todayDateString() {
  return new Date().toISOString().slice(0, 10);
}

export function yesterdayDateString() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}
